var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { mirrorX } from "apparatus/draw";
import { INFINITE_BOUNDS } from "apparatus/driver";
import { P, Segments } from "apparatus/library/common";
import { BasicShapes } from "apparatus/library/shapes/common";
import { Path } from "paper";
/** Minimum gap in paper pixels, between the two arrow heads or head and an end. */
const MINIMUM_GAP = 10;
function isV1(a) {
    return a.length !== undefined;
}
export class Arrow {
    constructor() {
        this.drivers = [
            Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ end1x: p.x, end1y: p.y }), fromAppearance: a => P(a.end1x, a.end1y), scale: 1.0 }),
            Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ end2x: p.x, end2y: p.y }), fromAppearance: a => P(a.end2x, a.end2y), scale: 1.0 }),
        ];
        this.shouldUseBoundsCenter = true;
    }
    static convertToLatest(input) {
        if (isV1(input)) {
            const { length } = input, rest = __rest(input, ["length"]);
            return Object.assign(Object.assign({}, rest), { end1x: -length / 2, end1y: 0, end2x: length / 2, end2y: 0 });
        }
        return input;
    }
    render(appearance) {
        // The arrow is drawn vertically, centered at (0, 0).
        // Hence, compute the transformation required, then draw the
        // arrow, then apply the transform.
        const startVector = P(appearance.end1x, appearance.end1y);
        const endVector = P(appearance.end2x, appearance.end2y);
        const deltaVector = endVector.subtract(startVector);
        const length = deltaVector.length;
        const rotation = deltaVector.angle;
        const midPoint = startVector.add(endVector).divide(2);
        const { width, headScale, headThickness, bothEnds } = appearance;
        const halfLength = length / 2;
        const maxHeadSize = Math.max(4, length - MINIMUM_GAP) / (bothEnds ? 2 : 1);
        const headSize = width * headScale * 2;
        const headFlare = headSize * headThickness;
        const effectiveHeadSize = Math.min(maxHeadSize, headSize);
        const sharpShift = effectiveHeadSize * (appearance.arrow === "sharp" ? 0.15 : 0.0);
        const graphic = new Path(Segments([[halfLength - effectiveHeadSize + sharpShift, -width / 2]], [[halfLength - effectiveHeadSize, -headFlare]], [[halfLength, 0]], // Tip
        [[halfLength - effectiveHeadSize, headFlare]], [[halfLength - effectiveHeadSize + sharpShift, width / 2]]));
        if (bothEnds) {
            mirrorX(graphic);
        }
        else {
            graphic.addSegments(Segments([[-halfLength, width / 2]], [[-halfLength, -width / 2]]));
        }
        graphic.closePath();
        graphic.rotate(rotation);
        graphic.position.x += midPoint.x;
        graphic.position.y += midPoint.y;
        BasicShapes.applyStyle(graphic, appearance);
        return {
            graphic,
            pivot: P(0, 0),
            hitShape: graphic.rectHitShape(),
        };
    }
}
Arrow.properties = {
    label: "Arrow",
    defaultAppearance: Object.assign({ end1x: -75, end1y: 0, end2x: 75, end2y: 0, width: 30, bothEnds: false, headScale: 1.0, headThickness: 0.5, arrow: "flat" }, BasicShapes.defaultAppearance),
    appearanceSpecs: [
        ...BasicShapes.specs,
        BasicShapes.header,
        {
            spec: "slider", key: "width", label: "Width",
            min: 4, max: 50,
        },
        {
            spec: "radio", key: "arrow", label: "Arrow style",
            options: [
                { value: "flat", label: "Flat" },
                { value: "sharp", label: "Sharp", tier: "boost" },
            ],
            allowSelectingTierLockedOptions: true,
        },
        {
            spec: "slider", key: "headScale", label: "Arrow scale",
            min: 1.0, max: 2, step: 0.01, unit: "%"
        },
        {
            spec: "slider", key: "headThickness", label: "Arrow thickness",
            min: 0.25, max: 1.0, step: 0.01, unit: "%"
        },
        {
            spec: "checkbox", key: "bothEnds", label: "Arrows on both ends"
        },
    ],
    isBoostRequired: a => a.arrow === "sharp"
};
